import { ArticleHelpfulState } from '@wix/answers-api';
import { Box, Text, TextButton, ToggleButton } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { ThumbsUpSmall, ThumbsDownSmall } from '@wix/wix-ui-icons-common';
import { FunctionComponent } from 'react';
import { animateScroll } from 'react-scroll';

import { DATA_HOOKS } from '../../../dataHooks';
import useIsHelpCenterCopilotEnabled from '../../../hooks/useIsHelpCenterCopilotEnabled';
import useIsHelpmateImprovedFeedbackEnabled from '../../../hooks/useIsHelpmateImprovedFeedbackEnabled';

import css from './index.module.scss';

export type VotingVariationAProps = {
  vote: ArticleHelpfulState;
  onVote: (vote: ArticleHelpfulState) => void;
  hideFeedbackAfterDelay?: () => void;
};

export const VotingVariationA: FunctionComponent<VotingVariationAProps> = ({
  vote,
  onVote,
  hideFeedbackAfterDelay,
}: VotingVariationAProps) => {
  const { t } = useTranslation();
  const isHelpCenterCopilotEnabled = useIsHelpCenterCopilotEnabled();
  const isPositiveFeedbackSent = vote === ArticleHelpfulState.HELPFUL;
  const isNegativeFeedbackSent = vote === ArticleHelpfulState.NOT_HELPFUL;
  const isHelpmateImprovedFeedbackEnabled =
    useIsHelpmateImprovedFeedbackEnabled();

  const helpfulClick = () => {
    onVote(ArticleHelpfulState.HELPFUL);
    hideFeedbackAfterDelay?.();
  };

  const notHelpfulClick = () => {
    if (isHelpCenterCopilotEnabled) {
      animateScroll.scrollToTop({ duration: 400, smooth: true });
    }
    onVote(ArticleHelpfulState.NOT_HELPFUL);
  };

  return !isHelpmateImprovedFeedbackEnabled ? (
    <Box direction="horizontal" gap="12px">
      <TextButton
        size="medium"
        weight={'thin'}
        onClick={helpfulClick}
        dataHook={DATA_HOOKS.HELPFUL_YES}
        className={`${css.textButton} ${
          isPositiveFeedbackSent ? css.selected : ''
        }`}
      >
        {t('helpful.button.yes')}
      </TextButton>
      <Text size="medium" weight="thin" className={css.separator}>
        |
      </Text>
      <TextButton
        size="medium"
        weight={'thin'}
        onClick={notHelpfulClick}
        dataHook={DATA_HOOKS.HELPFUL_NO}
        className={`${css.textButton} ${
          isNegativeFeedbackSent ? css.selected : ''
        }`}
      >
        {t('helpful.button.no')}
      </TextButton>
    </Box>
  ) : (
    <Box direction="horizontal" gap="6px" width="max-content">
      <ToggleButton
        size="small"
        border
        labelValue={t('helpful.button.yes')}
        shape="pill"
        onClick={helpfulClick}
        selected={isPositiveFeedbackSent}
      >
        <ThumbsUpSmall />
      </ToggleButton>
      <ToggleButton
        size="small"
        border
        labelValue={t('helpful.button.no')}
        shape="pill"
        onClick={notHelpfulClick}
        selected={isNegativeFeedbackSent}
      >
        <ThumbsDownSmall />
      </ToggleButton>
    </Box>
  );
};
